import React from 'react'
import { MsalProvider, useMsal } from '@azure/msal-react'

import { msalInstance } from './MSALConfig'
import useLogin from '../hooks/useLogin'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Loader2Icon } from 'lucide-react'
import {
  authenticateWithTeams,
  getTeamsSsoToken,
  isRunningInTeams,
} from './teams'

const AZURE_BACKEND_CLIENT_ID = import.meta.env.VITE_AZURE_BACKEND_CLIENT_ID

type SignInWithMicrosoftProps = {
  disabled?: boolean
  onLoadingChange?: (loading: boolean) => void
}

/**
 * Renders a button that allows users to sign in with their Microsoft account using MSAL authentication.
 *
 * When clicked, the button triggers a Microsoft login popup and, upon successful authentication,
 * passes the acquired access token to the application's login handler.
 */
export const SignInWithMicrosoft: React.FC<SignInWithMicrosoftProps> = ({
  disabled = false,
  onLoadingChange,
}) => {
  const { instance } = useMsal()
  const [popupLoading, setPopupLoading] = React.useState(false)

  const { handleMicrosoftLogin, loading } = useLogin({ variant: 'microsoft' })
  const translations = useTranslate({
    buttonText: 'login.sign-in-with-microsoft',
  })
  const isLoading = popupLoading || loading

  React.useEffect(() => {
    onLoadingChange?.(isLoading)
  }, [isLoading, onLoadingChange])

  const handleSignInWithMicrosoft = () => {
    if (disabled || isLoading) return

    setPopupLoading(true)

    async function signIn() {
      if (await isRunningInTeams()) {
        let accessToken: string
        try {
          accessToken = await getTeamsSsoToken()
        } catch (error) {
          console.warn('Teams SSO token acquisition failed, falling back.', error)
          accessToken = await authenticateWithTeams()
        }

        handleMicrosoftLogin(accessToken)
        return
      }

      const response = await instance.loginPopup({
        scopes: [`${AZURE_BACKEND_CLIENT_ID}/.default`],
      })
      handleMicrosoftLogin(response.accessToken)
    }

    signIn()
      .catch(error => {
        console.error(error)
      })
      .finally(() => {
        setPopupLoading(false)
      })
  }

  return (
    <button
      type="button"
      onClick={handleSignInWithMicrosoft}
      disabled={disabled || isLoading}
      aria-label={translations.buttonText}
      className="relative disabled:cursor-not-allowed disabled:opacity-60"
    >
      <img
        src="https://learn.microsoft.com/en-us/entra/identity-platform/media/howto-add-branding-in-apps/ms-symbollockup_signin_light.svg"
        alt=""
        className={isLoading ? 'cursor-wait opacity-30' : 'cursor-pointer'}
      />
      {isLoading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Loader2Icon className="size-6 animate-spin text-gray-600" />
        </span>
      )}
    </button>
  )
}

/**
 * Wrapper component to provide Msal context
 */
const Wrapper: React.FC<SignInWithMicrosoftProps> = props => {
  return (
    <MsalProvider instance={msalInstance}>
      <SignInWithMicrosoft {...props} />
    </MsalProvider>
  )
}

export default Wrapper
